import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootStore } from 'store/types';
import { NoGapGrid } from 'components/shared/NoGapGrid';
import { BoldBodyText, Button, Grid, Header3Text, PageLoading, SideSheet, SmallBodyText, SpacerRow } from 'ui-toolkit';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IUserData } from 'types/users/IUser';
import { getUserName } from 'utilities/users/getUserName';
import { DocumentHistory } from './DocumentHistory';
import {
  getCategoryLabelByName,
  requiresGoodThroughDate,
} from '../../../../types/order/documents/IDocumentCategoryType';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../../API/documentCategories/DocumentCategoriesApi';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';
import { useHasFeatureFlag } from '../../../../selectors/useHasFeatureFlag';

interface IDocumentDetailsSideSheetContainerProps {
  onClose: () => void;
  document: IStandardDocumentVersionData;
  isAmericanDigital: boolean;
  onShowGoodThroughDetails: () => void;
  users: IUserData[];
  setPreviewDoc: (val: IStandardDocumentVersionData | null) => void;
  // setDeleteDocument: (val: IStandardDocumentVersionData | null) => void;
}

export const DocumentDetailsSideSheetContainer: React.FC<IDocumentDetailsSideSheetContainerProps> = ({
  onClose,
  document,
  isAmericanDigital,
  onShowGoodThroughDetails,
  users,
  setPreviewDoc,
  // setDeleteDocument,
}) => {
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const { documentHistory, documentsHistoryFetchStatus } = useSelector(
    (state: RootStore) => state.order.documents.adminDocuments
  );
  const { data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  return (
    <SideSheet onClose={() => onClose()} title={<Header3Text>Document Details</Header3Text>}>
      {!documentsHistoryFetchStatus.success && !document?.isRepeatable && (
        <PageLoading isLoading={!documentsHistoryFetchStatus.success} />
      )}
      {showHistory ? (
        <DocumentHistory
          document={document}
          documentsHistoryFetchStatus={documentsHistoryFetchStatus}
          documentHistory={documentHistory}
          users={users}
          onBackClick={() => setShowHistory(false)}
          setPreviewDoc={setPreviewDoc}
          isZillowClosingEnabled={isZillowClosingEnabled}
          // setDeleteDocument={setDeleteDocument}
        />
      ) : (
        <Grid>
          {!document?.isRepeatable ? (
            <BoldBodyText>{document?.fileName}</BoldBodyText>
          ) : (
            <StyledBoldBodyText>{document?.fileName}</StyledBoldBodyText>
          )}
          <SpacerRow />
          <Grid columnCount={2}>
            <Grid textAlign={'left'}>
              <SmallBodyText intent={'faded'}>Category</SmallBodyText>
            </Grid>
            <Grid textAlign={'right'}>
              <SmallBodyText>{getCategoryLabelByName(document.category, documentCategoriesByName)}</SmallBodyText>
            </Grid>
            <Grid textAlign={'left'}>
              <SmallBodyText intent={'faded'}>Combined Document</SmallBodyText>
            </Grid>
            <Grid textAlign={'right'}>
              <SmallBodyText>{document?.metadata?.merged_documents ? 'Yes' : 'No'}</SmallBodyText>
            </Grid>
            <Grid textAlign="left">
              <SmallBodyText intent={'faded'}>Updated At</SmallBodyText>
            </Grid>
            <Grid textAlign={'right'}>
              {document?.updatedAt ? (
                <SmallBodyText>
                  {getDateString(DateFormat.shortDateTime, document?.updatedAt, false)} by{' '}
                  {getUserName(users, isZillowClosingEnabled, document.uploaderType, document.uploaderId)}
                </SmallBodyText>
              ) : (
                <SmallBodyText>-</SmallBodyText>
              )}
            </Grid>
            <Grid textAlign={'left'}>
              <SmallBodyText intent={'faded'}>Shared With Vendor</SmallBodyText>
            </Grid>
            <Grid textAlign={'right'}>
              {document?.vendorPublishedAt ? (
                <SmallBodyText>
                  {getDateString(DateFormat.shortDateTime, document?.vendorPublishedAt, false)} by{' '}
                  {getUserName(users, isZillowClosingEnabled, document.vendorPublisherType, document.vendorPublisherId)}
                </SmallBodyText>
              ) : (
                <SmallBodyText>-</SmallBodyText>
              )}
            </Grid>
            <Grid textAlign={'left'}>
              <SmallBodyText intent={'faded'}>Shared With Client</SmallBodyText>
            </Grid>
            <Grid textAlign={'right'}>
              {document?.clientPublishedAt ? (
                <SmallBodyText>
                  {getDateString(DateFormat.shortDateTime, document?.clientPublishedAt, false)} by{' '}
                  {getUserName(users, isZillowClosingEnabled, document.clientPublisherType, document.clientPublisherId)}
                </SmallBodyText>
              ) : (
                <SmallBodyText>-</SmallBodyText>
              )}
            </Grid>
            {!(isAmericanDigital && document?.category.includes('policy')) &&
              requiresGoodThroughDate(document?.category, documentCategoriesByName) && (
                <>
                  <Grid textAlign="left">
                    <SmallBodyText intent={'faded'}>Good Through</SmallBodyText>
                  </Grid>
                  <Grid textAlign="right">
                    <Button intent={'ghost'} isInline onClick={() => onShowGoodThroughDetails()}>
                      Details
                    </Button>
                  </Grid>
                </>
              )}

            {!document?.isRepeatable && documentsHistoryFetchStatus.success && (
              <>
                <NoGapGrid textAlign={'left'}>
                  <SmallBodyText intent={'faded'}>History</SmallBodyText>
                  <div>
                    <Button onClick={() => setShowHistory(true)} intent={'ghost'} isInline>
                      View All
                    </Button>
                  </div>
                </NoGapGrid>
                <Grid textAlign={'right'}>
                  {document?.id &&
                    documentsHistoryFetchStatus.success &&
                    !!documentHistory[document.id].length &&
                    documentHistory[document.id].slice(0, 2).map((v) => {
                      return (
                        <SmallBodyText key={v.id}>
                          {getDateString(DateFormat.shortDateTime, v.fileUpdatedAt, false)} by{' '}
                          {getUserName(users, isZillowClosingEnabled, v.uploaderType, v.uploaderId)}
                        </SmallBodyText>
                      );
                    })}
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      )}
    </SideSheet>
  );
};

const StyledBoldBodyText = styled(BoldBodyText)`
  width: 330px;
  overflow-wrap: anywhere;
`;
